import React from 'react';
import { Routes, Route } from 'react-router-dom';
import LoginPage from './pages/auth/LoginPage';
import HomePage from './pages/HomePage';
import FieldPage from './pages/fields/FieldPage';
import PlotPage from './pages/plots/PlotPage';
import PlotPlanEditor from './pages/plots/PlotPlanEditor';
import FieldPlanEditor from './pages/fields/FieldPlanEditor';
import BedPage from './pages/beds/BedPage';
import InvitePage from './pages/auth/InvitePage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminCropAttributesPage from './pages/admin/AdminCropAttributesPage';
import AdminAttributesCatalogPage from './pages/admin/AdminAttributesCatalogPage';
import AdminSystemMetricsPage from './pages/admin/AdminSystemMetricsPage';
import AdminSeasonsPage from './pages/admin/AdminSeasonsPage';
import ScenarioDebugPage from './pages/admin/ScenarioDebugPage';
import ScenarioCatalogPage from './pages/admin/ScenarioCatalogPage';
import ScenarioAuthorPage from './pages/admin/ScenarioAuthorPage';
import AdminEventTemplatesPage from './pages/admin/AdminEventTemplatesPage';
import AdminCultivationCardsPage from './pages/admin/AdminCultivationCardsPage';
import PlantingsPage from './pages/plantings/PlantingsPage';
import PlantingDetailsPage from './pages/plantings/PlantingDetailsPage';
import FarmStoragePage from './pages/plantings/FarmStoragePage';
import FarmPropertiesPage from './pages/properties/FarmPropertiesPage';
import FarmCropsPage from './pages/crops/FarmCropsPage';
import FarmCropDetailsPage from './pages/crops/FarmCropDetailsPage';
import FarmCropPlantingsPage from './pages/crops/FarmCropPlantingsPage';
import FarmAttributesPage from './pages/attributes/FarmAttributesPage';
import { EventQueuePage } from './pages/events/EventQueuePage';
import { BatchEventPage } from './pages/events/BatchEventPage';
import SimulationPage from './pages/simulation/SimulationPage';
import SavesListPage from './pages/saves/SavesListPage';
import GardenPlansPage from './pages/plans/GardenPlansPage';
import SaveDetailPage from './pages/saves/SaveDetailPage';
import SaveUsersPage from './pages/saves/SaveUsersPage';
import ScenariosGalleryPage from './pages/scenarios/ScenariosGalleryPage';
import SaveRouteGate from './routes/SaveRouteGate';
import { AuthRedirectHandler, RequireAdmin, RequireAuth } from './routes/AuthGuard';

/**
 * Save is the public root. Farm is an internal world container resolved from
 * `/saves/:saveId/world`, never a user-facing route.
 */
const AppRoutes: React.FC = () => {
  const withAuth = (element: React.ReactElement) => <RequireAuth>{element}</RequireAuth>;
  const withAdmin = (element: React.ReactElement) => (
    <RequireAuth><RequireAdmin permission="users">{element}</RequireAdmin></RequireAuth>
  );
  const withAttributesAdmin = (element: React.ReactElement) => (
    <RequireAuth><RequireAdmin permission="attributes">{element}</RequireAdmin></RequireAuth>
  );

  return (
    <>
      <AuthRedirectHandler />
      <Routes>
        <Route path="/" element={withAuth(<HomePage />)} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/invite/:token" element={<InvitePage />} />

        {/* Scenarios gallery (templates). */}
        <Route path="/scenarios" element={withAuth(<ScenariosGalleryPage />)} />

        {/* Saves list + overview. */}
        <Route path="/saves" element={withAuth(<SavesListPage />)} />
        <Route path="/plans" element={withAuth(<GardenPlansPage />)} />

        {/* Save-scoped routes — единый игровой root. SaveRouteGate подгружает
            save+world и кладёт в WorldContext; страницы достают данные через
            useSave/useWorld хуки. */}
        <Route path="/saves/:saveId" element={withAuth(<SaveRouteGate />)}>
          <Route index element={<SaveDetailPage />} />
          <Route path="users" element={<SaveUsersPage />} />
          <Route path="plantings" element={<PlantingsPage />} />
          <Route path="plantings/:plantingId" element={<PlantingDetailsPage />} />
          <Route path="storage" element={<FarmStoragePage />} />
          <Route path="properties" element={<FarmPropertiesPage />} />
          <Route path="attributes" element={<FarmAttributesPage />} />
          <Route path="crops" element={<FarmCropsPage />} />
          <Route path="crops/:cropId" element={<FarmCropDetailsPage />} />
          <Route path="crops/:cropId/plantings" element={<FarmCropPlantingsPage />} />
          <Route path="tasks" element={<EventQueuePage />} />
          <Route path="events/:eventId" element={<BatchEventPage />} />
          <Route path="simulation" element={<SimulationPage />} />
          <Route path="plots/:plotId/plan" element={<PlotPlanEditor />} />
          <Route path="plots/:plotId/info" element={<PlotPage />} />
          <Route path="fields/:fieldId/plan" element={<FieldPlanEditor />} />
          <Route path="fields/:fieldId/info" element={<FieldPage />} />
          <Route path="beds/:bedId" element={<BedPage />} />
        </Route>

        {/* Admin routes (cross-cutting). Веб-клиент — прежде всего админский:
            авторинг, тонкая настройка и отладка сценариев живут здесь, тогда как
            играют в них из Unity. */}
        <Route path="/admin/users" element={withAdmin(<AdminUsersPage />)} />
        <Route path="/admin/crop-attributes" element={withAttributesAdmin(<AdminCropAttributesPage />)} />
        <Route path="/admin/attributes" element={withAttributesAdmin(<AdminAttributesCatalogPage />)} />
        <Route path="/admin/system" element={withAdmin(<AdminSystemMetricsPage />)} />
        <Route path="/admin/seasons" element={withAdmin(<AdminSeasonsPage />)} />
        <Route path="/admin/scenarios" element={withAdmin(<ScenarioCatalogPage />)} />
        <Route path="/admin/scenarios/author" element={withAdmin(<ScenarioAuthorPage />)} />
        <Route path="/admin/scenario-debug" element={withAdmin(<ScenarioDebugPage />)} />
        <Route path="/admin/event-templates" element={withAttributesAdmin(<AdminEventTemplatesPage />)} />
        <Route path="/admin/cultivation-cards" element={withAttributesAdmin(<AdminCultivationCardsPage />)} />
      </Routes>
    </>
  );
};

export default AppRoutes;
